import { apiClient } from './client';
import type {
  KnowledgeExploration,
  CreateExplorationDto,
  ExplorationGraph,
  ExplorationViewMode,
  ConceptEvidence,
  InstanceEvidence,
  GraphKpiId,
  GraphKpiReport,
  GraphKpiRowsPage,
} from '@snxt/snxt-onto-share';

// The apiClient already unwraps the { success, data } envelope and returns
// data.data directly, so we pass the inner type T as the generic argument.

// ─── Explorations CRUD ────────────────────────────────────────────────────────

export const explorationApi = {
  // `signal` exists for the verification reads in useResilientMutation: they need a much
  // shorter deadline than env.apiTimeout, and the api client uses a caller-supplied signal
  // instead of its own 30s controller.
  list: (signal?: AbortSignal) =>
    apiClient.get<KnowledgeExploration[]>('/api/explorations', { signal }),

  get: (id: string, signal?: AbortSignal) =>
    apiClient.get<KnowledgeExploration>(`/api/explorations/${id}`, { signal }),

  create: (dto: CreateExplorationDto & { populate?: boolean }) =>
    apiClient.post<KnowledgeExploration>('/api/explorations', dto),

  delete: (id: string) =>
    apiClient.delete(`/api/explorations/${id}`),

  // ── Populate pipeline ──────────────────────────────────────────────────────

  triggerPopulate: (id: string) =>
    apiClient.post(`/api/explorations/${id}/populate`, {}),

  // ── Graph + evidence ───────────────────────────────────────────────────────

  getGraph: (id: string, mode: ExplorationViewMode = 'ontology') =>
    apiClient.get<ExplorationGraph>(`/api/explorations/${id}/graph?mode=${mode}`),

  /** Persist the manual node layout of the explorer canvas (A4). */
  saveLayout: (id: string, layout: Record<string, { x: number; y: number }>) =>
    apiClient.patch(`/api/explorations/${id}/layout`, { layout }),

  getConceptEvidence: (explorationId: string, nodeId: string) =>
    apiClient.get<ConceptEvidence>(
      `/api/explorations/${explorationId}/concepts/${encodeURIComponent(nodeId)}/evidence`
    ),

  getInstanceEvidence: (explorationId: string, instanceId: string) =>
    apiClient.get<InstanceEvidence>(
      `/api/explorations/${explorationId}/instances/${encodeURIComponent(instanceId)}/evidence`
    ),

  // ── Graph quality KPIs (tier 1) ────────────────────────────────────────────

  /** The 14 tier-1 KPIs plus the contract, computed at read time for one exploration. */
  getKpis: (id: string) =>
    apiClient.get<GraphKpiReport>(`/api/explorations/${id}/kpis`),

  /**
   * The offending rows behind one KPI. Two endpoints and not one because the scalar on screen is
   * the cardinal of a set the user can OPEN, and a set that can hold thousands of rows must not be
   * inlined into the summary.
   */
  getKpiRows: (id: string, kpiId: GraphKpiId, limit: number, offset: number) =>
    apiClient.get<GraphKpiRowsPage>(
      `/api/explorations/${id}/kpis/${kpiId}/rows?limit=${limit}&offset=${offset}`
    ),
};

// Made with Bob
